"use client";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IFilter, FilterTypes } from "@/api/AppApi/services/filters/interfaces/IGetFilters";
import SelectOption from "../filter/filter-components/SelectOption";
import ColorFilter from "../filter/filter-components/ColorFilter";
import TagsFilter from "../filter/filter-components/TagsFilter";
import SelectButtons from "../filter/filter-components/SelectButtons";
import PriceRager from "../filter/filter-components/PriceRager";
import CustomButton, { BtnRadius, BtnSizes, BtnTypes } from "@/app/components/lib/CustomButton";
import { useTranslation } from "@/hooks/localization/useTranslations";

interface FilterChipDropdownProps {
  filter: IFilter;
  selectedValues: string[];
  onApply: (values: string[]) => void;
  onClose: () => void;
  isOpen: boolean;
  anchorElement?: HTMLElement | null;
}

const FilterChipDropdown: React.FC<FilterChipDropdownProps> = ({
  filter,
  selectedValues,
  onApply,
  onClose,
  isOpen,
  anchorElement,
}) => {
  const t = useTranslation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isAnimating, setIsAnimating] = useState(false);
  const [localValues, setLocalValues] = useState<string[]>(selectedValues);
  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties>({});

  useEffect(() => {
    setLocalValues(selectedValues);
  }, [selectedValues]);

  useEffect(() => {
    if (!isOpen || !anchorElement) return;

    const anchorRect = anchorElement.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current?.offsetHeight || 320;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - anchorRect.bottom;
    const spaceAbove = anchorRect.top;

    let pos: "below" | "above" = "below";
    if (spaceBelow < dropdownHeight + 4 && spaceAbove > spaceBelow) {
      pos = "above";
    }


    let calculatedLeft = anchorRect.left + window.scrollX;
    if (calculatedLeft + 280 > window.innerWidth + window.scrollX) {
      calculatedLeft = window.innerWidth + window.scrollX - 280 - 16;
    }
    if (calculatedLeft < window.scrollX + 16) {
      calculatedLeft = window.scrollX + 16;
    }

    setDropdownStyles({
      left: `${calculatedLeft}px`,
      ...(pos === "below"
        ? { top: `${anchorRect.bottom + window.scrollY + 4}px` }
        : { top: `${anchorRect.top + window.scrollY - dropdownHeight - 4}px` }),
    });
  }, [isOpen, anchorElement]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        anchorElement &&
        !anchorElement.contains(target)
      ) {
        onClose();
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, anchorElement]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    } else {
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen && !isAnimating) {
    return null;
  }

  const { configurations, data } = filter;
  const { title, type, key } = configurations;

  const handleFilterChange = (values: string[]) => {
    setLocalValues(values);
    
    // For non-range filters, apply immediately on change
    // For range sliders, wait for explicit apply (better UX for dragging)
    if (type !== FilterTypes.range_slider) {
      onApply(values);
    }
  };

  const handleApplyClick = () => {
    onApply(localValues);
    onClose();
  };

  const handleClearClick = () => {
    const emptyValues: string[] = [];
    setLocalValues(emptyValues);
    // Apply and close immediately for better UX
    onApply(emptyValues);
    onClose();
  };

  const renderFilterContent = () => {
    // For range_slider, data can be null - it uses min/max from configurations
    if (type !== FilterTypes.range_slider && (!data || data.length === 0)) {
      return (
        <div style={{ padding: "8px", color: "var(--body-color, #333333)", fontSize: "14px", textAlign: "center" }}>
          No options available
        </div>
      );
    }

    switch (type) {
      case FilterTypes.checkbox:
        return (
          <SelectOption
            filter={filter}
            state={localValues}
            setState={handleFilterChange}
            isFromFilters={true}
          />
        );

      case FilterTypes.color_selector:
        return (
          <ColorFilter
            filter={filter}
            state={localValues}
            setState={handleFilterChange}
            isFilterDrawer={true}
          />
        );

      case FilterTypes.tags:
      case FilterTypes.dropdown:
        return (
          <TagsFilter
            filter={filter}
            state={localValues}
            setState={handleFilterChange}
            isFromFilters={true}
          />
        );

      case FilterTypes.carousel:
        return (
          <SelectButtons
            filter={filter}
            state={localValues}
            setState={handleFilterChange}
            isFilterDrawer={true}
          />
        );

      case FilterTypes.images:
        return (
          <SelectOption
            filter={filter}
            state={localValues}
            setState={handleFilterChange}
            isFromFilters={true}
          />
        );

      case FilterTypes.range_slider:
        return (
          <PriceRager
            filter={filter}
            state={localValues}
            setState={handleFilterChange}
          />
        );

      default:
        return (
          <SelectOption
            filter={filter}
            state={localValues}
            setState={handleFilterChange}
            isFromFilters={true}
          />
        );
    }
  };

  const dynamicStyles = dropdownStyles;

  const dropdownContent = (
    <div
      ref={dropdownRef}
      id={`filter-dropdown-${key}`}
      className={`filter-chip-dropdown ${isOpen ? "slide-down" : "slide-up"}`}
      role="listbox"
      aria-label={`${title} options`}
      aria-multiselectable={configurations.is_multi_select}
      style={dynamicStyles}
    >
      {renderFilterContent()}

      <div className="dropdown-actions">
        {type === FilterTypes.range_slider ? (
          // For range slider, show both Clear and Apply buttons
          <>
            <CustomButton
              type={BtnTypes.venDolsy}
              size={BtnSizes.medium}
              radius={BtnRadius.small}
              onClick={handleClearClick}
              fullWidth
            >
              {t?.clear_filters || "Clear"}
            </CustomButton>
            <CustomButton
              type={BtnTypes.primary}
              size={BtnSizes.medium}
              radius={BtnRadius.small}
              onClick={handleApplyClick}
              fullWidth
            >
              {t?.apply || "Apply"}
            </CustomButton>
          </>
        ) : (
          // For other filters, show both Clear and Close buttons
          <>
            <CustomButton
              type={BtnTypes.venDolsy}
              size={BtnSizes.medium}
              radius={BtnRadius.small}
              onClick={handleClearClick}
              fullWidth
            >
              {t?.clear_filters || "Clear"}
            </CustomButton>
            <CustomButton
              type={BtnTypes.primary}
              size={BtnSizes.medium}
              radius={BtnRadius.small}
              onClick={onClose}
              fullWidth
            >
              {t?.close || "Close"}
            </CustomButton>
          </>
        )}
      </div>
    </div>
  );

  if (typeof document !== "undefined") {
    return createPortal(dropdownContent, document.body);
  }

  return dropdownContent;
};

export default FilterChipDropdown;
